import { useRef, useState } from 'react';
import type { ElementType, FC } from 'react';
import { subDays } from 'date-fns';
import {
  Avatar,
  Box,
  Button,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  Tooltip,
  Typography
} from '@material-ui/core';
import { BellIcon, AppBarIconButton, Badge } from "@a0/quantum-product";
import { experimentalStyled } from '@material-ui/core/styles';
import ChatAltIcon from '../../icons/ChatAlt';
import Exclamation from 'src/icons/Exclamation';

const DashboardAppBarIconButton = experimentalStyled(AppBarIconButton)`
  :hover {
    background-color: #505bb3
  }
`;

interface Notification {
  id: string;
  title: string;
  description: string;
  type: string;
  createdAt: number;
}

const now = new Date();

const notifications: Notification[] = [
  {
    id: '5e88840187f6b09b431bae68',
    createdAt: subDays(now, 7).getTime(),
    description: 'You have 4 recommendations for your team',
    title: 'Pending Recommendations',
    type: 'new_message'
  },
  {
    id: '5e88840187f6b09b431bae61',
    createdAt: subDays(now, 7).getTime(),
    description: 'Your team is now green!',
    title: 'Team status change',
    type: 'exclamation'
  }
];

const iconsMap: Record<string, ElementType> = {
  new_message: ChatAltIcon,
  exclamation: Exclamation
};

const NotificationsPopover: FC = () => {
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState<boolean>(false);

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  return (
    <>
      <Tooltip title="Notifications">
        <DashboardAppBarIconButton
          color="inherit"
          ref={anchorRef}
          onClick={handleOpen}
        >
         <Badge
            badgeContent="4"
            color="danger"
            overlap="rectangular"
            variant="dot"
          >
             <BellIcon size="1em" />
          </Badge>
        </DashboardAppBarIconButton>
      </Tooltip>
      <Popover
        anchorEl={anchorRef.current}
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'bottom'
        }}
        onClose={handleClose}
        open={open}
        PaperProps={{
          sx: { width: 320 }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography
            color="textPrimary"
            variant="h6"
          >
            Notifications
          </Typography>
        </Box>
        {
          notifications.length === 0
            ? (
              <Box sx={{ p: 2 }}>
                <Typography
                  color="textPrimary"
                  variant="subtitle2"
                >
                  There are no notifications
                </Typography>
              </Box>
            )
            : (
              <>
                <List disablePadding>
                  {notifications.map((notification) => {
                    const Icon = iconsMap[notification.type];

                    return (
                      <ListItem
                        divider
                        key={notification.id}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              backgroundColor: 'primary.main',
                              color: 'primary.contrastText'
                            }}
                          >
                            <Icon fontSize="small" />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={(
                            <Link
                              color="textPrimary"
                              sx={{ cursor: 'pointer' }}
                              underline="none"
                              variant="subtitle2"
                            >
                              {notification.title}
                            </Link>
                          )}
                          secondary={notification.description}
                        />
                      </ListItem>
                    );
                  })}
                </List>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    p: 1
                  }}
                >
                  <Button
                    color="primary"
                    size="small"
                    variant="text"
                  >
                    Mark all as read
                  </Button>
                </Box>
              </>
            )
        }
      </Popover>
    </>
  );
};

export default NotificationsPopover;
